/**
 * Session Timeout Handler
 * 
 * WHY: NIST 800-171 3.1.11 mandates session termination after a defined
 * period of inactivity. For DoD applications, this is typically 15 minutes.
 * 
 * This component:
 * 1. Tracks user activity (mouse, keyboard, touch, scroll)
 * 2. Shows a warning modal at 14 minutes of inactivity
 * 3. Automatically logs the user out at 15 minutes
 * 4. Provides "Stay Logged In" and "Logout Now" actions
 * 
 * COMPLIANCE:
 * - NIST 800-171 3.1.11: Session Lock
 * - CMMC AC.L2-3.1.11: Session Termination
 * 
 * @see .docs/architecture/phase4_compliance_assessment.md - Expert 5
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Clock, LogOut, RefreshCw } from 'lucide-react'

/**
 * Configuration for session timeout (in milliseconds).
 * WHY: Constants are defined here for easy adjustment across deployments.
 */
const SESSION_CONFIG = {
  /** Total idle time before automatic logout (15 minutes) */
  TIMEOUT_MS: 15 * 60 * 1000,
  /** Time before timeout to show warning (1 minute before) */
  WARNING_MS: 14 * 60 * 1000,
  /** How often to check for timeout (every 10 seconds) */
  CHECK_INTERVAL_MS: 10 * 1000,
  /** Activity events that reset the idle timer */
  ACTIVITY_EVENTS: ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'] as const,
}

interface SessionTimeoutHandlerProps {
  /** Override default timeout (for testing) */
  timeoutMs?: number
  /** Override default warning time (for testing) */
  warningMs?: number
  /** Callback when session times out (in addition to logout) */
  onTimeout?: () => void
}

export function SessionTimeoutHandler({
  timeoutMs = SESSION_CONFIG.TIMEOUT_MS,
  warningMs = SESSION_CONFIG.WARNING_MS,
  onTimeout,
}: SessionTimeoutHandlerProps) {
  const { isAuthenticated, logout } = useAuth()
  const navigate = useNavigate()
  
  const [showWarning, setShowWarning] = useState(false)
  const [remainingSeconds, setRemainingSeconds] = useState(0)
  
  // Track last activity time using ref to avoid re-renders
  const lastActivityRef = useRef(Date.now())
  const warningIntervalRef = useRef<NodeJS.Timeout | null>(null)

  /**
   * Reset the idle timer on user activity.
   * WHY: Any user interaction should be considered "activity" and reset the timer.
   */
  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now()
    setShowWarning(false)
    
    // Clear the countdown interval if running
    if (warningIntervalRef.current) {
      clearInterval(warningIntervalRef.current)
      warningIntervalRef.current = null
    }
  }, [])

  /**
   * Handle session timeout.
   * WHY: When the timeout is reached, we must securely log the user out
   * and redirect to the login page.
   */
  const handleTimeout = useCallback(() => {
    onTimeout?.()
    logout()
    navigate('/login', { 
      state: { 
        message: 'Your session has expired due to inactivity. Please log in again.',
        reason: 'session_timeout' 
      } 
    })
  }, [logout, navigate, onTimeout])

  /**
   * Handle user choosing to stay logged in.
   * WHY: Clicking "Stay Logged In" is an activity event that resets the timer.
   */
  const handleStayLoggedIn = useCallback(() => {
    resetTimer()
  }, [resetTimer])

  /**
   * Handle user choosing to logout immediately.
   */
  const handleLogoutNow = useCallback(() => {
    logout()
    navigate('/login')
  }, [logout, navigate])

  // Set up activity event listeners
  useEffect(() => {
    if (!isAuthenticated) return

    const handleActivity = () => {
      resetTimer()
    }

    // Add event listeners for all activity types
    SESSION_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true })
    })

    return () => {
      SESSION_CONFIG.ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity)
      })
    }
  }, [isAuthenticated, resetTimer])

  // Set up timeout checking interval
  useEffect(() => {
    if (!isAuthenticated) return

    const checkTimeout = () => {
      const now = Date.now()
      const idleTime = now - lastActivityRef.current

      if (idleTime >= timeoutMs) {
        // Session has timed out
        handleTimeout()
      } else if (idleTime >= warningMs && !showWarning) {
        // Show warning and start countdown
        setShowWarning(true)
        
        // Start countdown interval for the remaining time display
        warningIntervalRef.current = setInterval(() => {
          const remaining = Math.max(0, timeoutMs - (Date.now() - lastActivityRef.current))
          setRemainingSeconds(Math.ceil(remaining / 1000))
          
          if (remaining <= 0) {
            handleTimeout()
          }
        }, 1000)
      }
    }

    const intervalId = setInterval(checkTimeout, SESSION_CONFIG.CHECK_INTERVAL_MS)

    return () => {
      clearInterval(intervalId)
      if (warningIntervalRef.current) {
        clearInterval(warningIntervalRef.current)
      }
    }
  }, [isAuthenticated, timeoutMs, warningMs, showWarning, handleTimeout])

  // Only render if authenticated and warning is active
  if (!isAuthenticated || !showWarning) {
    return null
  }

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-description"
    >
      <Card className="w-full max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto border-destructive/50 shadow-2xl animate-in zoom-in-95">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
            <Clock className="h-8 w-8 text-destructive" />
          </div>
          <CardTitle id="session-timeout-title" className="text-xl">
            Session Timeout Warning
          </CardTitle>
          <CardDescription id="session-timeout-description" className="text-base">
            Your session will expire due to inactivity.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Countdown display */}
          <div className="text-center">
            <div className="text-4xl font-bold tabular-nums text-destructive">
              {Math.floor(remainingSeconds / 60)}:{String(remainingSeconds % 60).padStart(2, '0')}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              Time remaining before automatic logout
            </p>
          </div>

          {/* Compliance notice */}
          <p className="text-xs text-muted-foreground text-center">
            Per NIST 800-171 security requirements, sessions are automatically 
            terminated after 15 minutes of inactivity.
          </p>

          {/* Action buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleLogoutNow}
              className="flex-1 gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout Now
            </Button>
            <Button
              onClick={handleStayLoggedIn}
              className="flex-1 gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Stay Logged In
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

/**
 * Hook to get session timeout status.
 * WHY: Other components may need to know if a timeout warning is active.
 */
export function useSessionTimeout() {
  const [isWarningActive, setIsWarningActive] = useState(false)
  
  // This could be expanded to read from a global state if needed
  return { isWarningActive, setIsWarningActive }
}
